/** @format */

import {Controller, FormProvider, useForm, useWatch} from 'react-hook-form';

import {BugReportOutlined, ChatBubbleOutline} from '@mui/icons-material';

import {yupResolver} from '@hookform/resolvers/yup';

import {CustomRating} from 'components/InputsNew/CustomRating';

import {CustomToggleButtonGroup} from 'components/buttons/CustomToggleButtonGroup';

import {BlueButton} from 'components/buttons/BlueButton';

import {useApolloError} from 'helpers/hooks/useApolloError';

import {SimpleButton} from 'components/buttons/SimpleButton';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {
  LabelBox,
  LabelText,
  QuestionBox,
  QuestionGroup,
  RegularText,
  RegularTextBox,
  RequiredBadge,
  Root,
  StyledTextFieldWrap,
  SubmitButtons,
  TextareaBox,
  TextareaIconBox,
  TextareaQuestionBox,
} from './styles';
import {
  DEFAULT_VALUES,
  DISLIKES_OPTIONS,
  HYPOTHESIS_OPTIONS,
  ID_CANCEL,
  ID_INPUT_BUG,
  ID_INPUT_COMMENT,
  ID_INPUT_DISLIKES,
  ID_INPUT_HYPOTHESIS,
  ID_INPUT_RATING,
  ID_ROOT,
  ID_SUBMIT,
  MAX_BUG_DESCRIPTION,
  MAX_COMMENT,
  MAX_SCREENSHOT_SIZE_MB,
  MAX_SCREENSHOTS,
} from './constants';
import {FormValues} from './types';
import {validationSchema} from './validationSchema';
import {ScreenshotUploader} from './components/ScreenshotUploader';
import {mapFormValues} from './helpers/mapFormValues';
import {useCreateAiReportFeedbackMutation} from './graphql/__generated__';

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
  aiReportId: string | null;
}

const FeedbackForm = ({onCancel, onSuccess, aiReportId}: Props) => {
  const {isError, onError, renderAlert} = useApolloError();
  const {
    currentCompany: {uuid: companyUuid},
  } = useCompanyContext();
  const {mixpanel} = useMixpanelContext();

  const methods = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: {isValid},
  } = methods;

  const dislikes = useWatch({name: 'dislikes', control});
  const showBugBlock = dislikes.includes('bug');

  const [createFeedback, {loading}] = useCreateAiReportFeedbackMutation();

  const disabled = loading || isError;

  const onSubmit = async (data: FormValues) => {
    const mappedData = mapFormValues(data, aiReportId, companyUuid);

    try {
      const response = await createFeedback({
        variables: {
          data: mappedData,
        },
      });
      if (response.data?.createAiReportFeedback) {
        mixpanel.track('ai_report_feedback_submitted', {
          order_id: aiReportId,
          marvin_id: aiReportId,
          project_uuid: companyUuid,
          rating: data.rating,
          hypothesis: data.hypothesis,
          has_bug: data.dislikes.includes('bug'),
          has_comment: Boolean(data.comment),
        });
        onSuccess();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      onError();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Root data-testid={ID_ROOT}>
          <QuestionBox>
            <LabelBox>
              <LabelText>Оценка отчета</LabelText>
              <RequiredBadge>Обязательно</RequiredBadge>
            </LabelBox>
            <Controller
              name='rating'
              control={control}
              render={({field}) => {
                const {onChange, ...rest} = field;
                return (
                  <CustomRating
                    onChange={(_, newValue) => {
                      onChange(newValue);
                    }}
                    disabled={disabled}
                    {...rest}
                    data-testid={ID_INPUT_RATING}
                  />
                );
              }}
            />
          </QuestionBox>

          <QuestionBox>
            <LabelBox>
              <LabelText>Есть ли полезные гипотезы?</LabelText>
              <RequiredBadge>Обязательно</RequiredBadge>
            </LabelBox>
            <Controller
              name='hypothesis'
              control={control}
              render={({field: {value, onChange}}) => (
                <CustomToggleButtonGroup
                  value={value}
                  onChange={onChange}
                  options={HYPOTHESIS_OPTIONS}
                  disabled={disabled}
                  data-testid={ID_INPUT_HYPOTHESIS}
                />
              )}
            />
          </QuestionBox>

          <QuestionBox>
            <LabelBox>
              <LabelText>Что не понравилось?</LabelText>
            </LabelBox>
            <Controller
              name='dislikes'
              control={control}
              render={({field: {value, onChange}}) => (
                <CustomToggleButtonGroup
                  value={value}
                  onChange={onChange}
                  options={DISLIKES_OPTIONS}
                  disabled={disabled}
                  multiple
                  data-testid={ID_INPUT_DISLIKES}
                />
              )}
            />
          </QuestionBox>

          {showBugBlock && (
            <QuestionBox>
              <LabelBox>
                <RegularText>Опишите ошибку, чтобы поскорей ее исправили</RegularText>
                <RequiredBadge>Обязательно</RequiredBadge>
              </LabelBox>

              <QuestionGroup>
                <Controller
                  name='bugDescription'
                  control={control}
                  render={({field}) => (
                    <TextareaBox>
                      <StyledTextFieldWrap
                        {...field}
                        placeholder='Кратко об ошибке или баге'
                        disabled={disabled}
                        multiline
                        maxRows={5}
                        paddingBottom={3}
                        maxLength={MAX_BUG_DESCRIPTION}
                        slotProps={{
                          htmlInput: {
                            ['data-testid']: ID_INPUT_BUG,
                          },
                        }}
                      />
                      <TextareaIconBox>
                        <BugReportOutlined />
                      </TextareaIconBox>
                    </TextareaBox>
                  )}
                />

                <Controller
                  name='screenshots'
                  control={control}
                  render={() => (
                    <ScreenshotUploader
                      control={control}
                      disabled={disabled}
                      maxFiles={MAX_SCREENSHOTS}
                      maxSizeMb={MAX_SCREENSHOT_SIZE_MB}
                    />
                  )}
                />
              </QuestionGroup>
            </QuestionBox>
          )}

          <TextareaQuestionBox>
            <RegularTextBox>
              <RegularText>Напишите нам свое мнение об отчете.</RegularText>
              <RegularText>
                Мы рассматриваем все комментарии и постоянно улучшаем качество аналитики.
              </RegularText>
            </RegularTextBox>

            <Controller
              name='comment'
              control={control}
              render={({field}) => (
                <TextareaBox>
                  <StyledTextFieldWrap
                    {...field}
                    placeholder='Критика, предложения, пожелания'
                    disabled={disabled}
                    multiline
                    maxRows={5}
                    maxLength={MAX_COMMENT}
                    slotProps={{
                      htmlInput: {
                        ['data-testid']: ID_INPUT_COMMENT,
                      },
                    }}
                  />
                  <TextareaIconBox>
                    <ChatBubbleOutline />
                  </TextareaIconBox>
                </TextareaBox>
              )}
            />
          </TextareaQuestionBox>

          {renderAlert}

          <SubmitButtons>
            <BlueButton type='submit' disabled={!isValid || disabled} data-testid={ID_SUBMIT}>
              {isValid ? 'Подтвердить' : 'Заполните обязательные поля'}
            </BlueButton>
            <SimpleButton onClick={onCancel} disabled={disabled} data-testid={ID_CANCEL}>
              Отмена
            </SimpleButton>
          </SubmitButtons>
        </Root>
      </form>
    </FormProvider>
  );
};

export {FeedbackForm};
